import type { ListReleaseNotesQuery, ReleaseNote } from '@clockwise/clients/release-notes';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Filter, Pencil, Plus, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MarkdownContent } from '@/components/custom/markdown-content';
import { PaginationFooter } from '@/components/custom/pagination-footer';
import { PillBadge } from '@/components/custom/pill-badge';
import { ReleaseNoteFormSheet } from '@/components/custom/release-note-form-sheet';
import { RequirePermission } from '@/components/custom/require-permission';
import { SearchInput } from '@/components/custom/search-input';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { usePageWindow } from '@/hooks/use-page-window';
import { releaseNotesApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

type StatusFilter = 'active' | 'inactive' | 'all';
const PAGE_SIZE = 12;

/**
 * The What's New tab: a card feed of release notes (not a table, unlike
 * Announcements — see the request that shaped this). Visible to every
 * signed-in user with no permission needed; Add/Edit/Delete/Restore gated on
 * `release_note.edit`.
 */
export function ReleaseNotesPanel() {
  const queryClient = useQueryClient();
  const canEdit = useHasPermission('release_note.edit');

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<ReleaseNote | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const q = useDebouncedValue(search.trim(), 300);

  useEffect(() => {
    setPage(0);
  }, [q, status]);

  const listQuery: ListReleaseNotesQuery = {
    q: q || undefined,
    active: status === 'all' ? undefined : status === 'active',
    sortField: 'releasedAt',
    sortDirection: 'desc',
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.releaseNotes.list(listQuery),
    queryFn: () => releaseNotesApi.list(listQuery),
    placeholderData: keepPreviousData,
    // This list changes only on an admin mutation, which already invalidates
    // the query directly — a short staleTime just avoids an unnecessary
    // refetch when switching back to this tab, not staleness after an edit.
    staleTime: 30_000,
  });

  const notes = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;
  const { pageCount, shownOffset } = usePageWindow({
    page,
    setPage,
    pageSize: PAGE_SIZE,
    total,
    responseOffset: query.data?.meta.offset,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.releaseNotes.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => releaseNotesApi.remove(id),
    onSuccess: () => {
      toast.success('Release note deactivated');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Delete failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => releaseNotesApi.restore(id),
    onSuccess: () => {
      toast.success('Release note reactivated');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Restore failed'),
  });

  const openEditor = (note: ReleaseNote | null) => {
    setEditing(note);
    setSheetOpen(true);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search release name, version…"
          value={search}
          onValueChange={setSearch}
        />
        {canEdit ? (
          <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        ) : null}
        <div className="ml-auto">
          <RequirePermission permission="release_note.edit">
            <Button onClick={() => openEditor(null)}>
              <Plus />
              Add release note
            </Button>
          </RequirePermission>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {query.isLoading ? (
          <div className="grid gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={`skeleton-${i}`} className="h-40 rounded-xl" />
            ))}
          </div>
        ) : query.isError ? (
          <p className="py-10 text-center text-sm text-destructive">
            {query.error instanceof ApiError
              ? query.error.message
              : 'Failed to load release notes.'}
          </p>
        ) : notes.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            {q
              ? 'No release notes match this search.'
              : 'No release notes yet. Click “Add release note”.'}
          </p>
        ) : (
          <div className="grid gap-4">
            {notes.map((note) => (
              <Card key={note.id} className={!note.active ? 'opacity-60' : undefined}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    {note.releaseName}
                    <PillBadge
                      variant="outline"
                      className="border-transparent bg-status-ok text-status-ok-foreground font-mono text-[11px] font-medium tracking-tight"
                    >
                      {note.releaseVersion}
                    </PillBadge>
                    {!note.active ? (
                      <span className="text-xs font-normal text-muted-foreground">(inactive)</span>
                    ) : null}
                  </CardTitle>
                  <CardDescription>
                    Released {format(parseISO(note.releasedAt), 'PP')}
                  </CardDescription>
                  {canEdit ? (
                    <CardAction className="flex gap-1">
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        aria-label="Edit"
                        onClick={() => openEditor(note)}
                      >
                        <Pencil />
                      </Button>
                      {note.active ? (
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Deactivate"
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (confirm(`Deactivate "${note.releaseVersion}"?`)) {
                              deleteMutation.mutate(note.id);
                            }
                          }}
                        >
                          <Trash2 />
                        </Button>
                      ) : (
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Reactivate"
                          disabled={restoreMutation.isPending}
                          onClick={() => restoreMutation.mutate(note.id)}
                        >
                          <Undo2 />
                        </Button>
                      )}
                    </CardAction>
                  ) : null}
                </CardHeader>
                <CardContent>
                  {note.details ? (
                    <MarkdownContent>{note.details}</MarkdownContent>
                  ) : (
                    <p className="text-sm text-muted-foreground">No details provided.</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <PaginationFooter
        shownOffset={shownOffset}
        shownCount={notes.length}
        total={total}
        pageSize={PAGE_SIZE}
        page={page}
        pageCount={pageCount}
        isFetching={query.isFetching}
        onPrevious={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />

      <ReleaseNoteFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        releaseNote={editing}
      />
    </div>
  );
}
